import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Player, SkillProgress } from '../types';
import type { AuthUser, Organization } from '../lib/api';

type Theme = 'light' | 'dark';

interface AppState {
  // Theme
  theme: Theme;

  // Legacy player auth (for anonymous play)
  player: Player | null;
  isLoggedIn: boolean;

  // WorkOS Auth
  authUser: AuthUser | null;
  organizations: Organization[];
  isAuthenticated: boolean;
  authLoading: boolean;

  // Skill tracking (by category)
  skillProgress: Record<string, SkillProgress>;

  // Actions
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setPlayer: (player: Player | null) => void;
  logout: () => void;

  // WorkOS Auth actions
  setAuthState: (user: AuthUser | null, orgs: Organization[]) => void;
  setAuthLoading: (loading: boolean) => void;
  clearAuth: () => void;

  // Skill tracking
  updateSkillProgress: (category: string, correct: boolean) => void;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      theme: 'light',
      player: null,
      isLoggedIn: false,
      skillProgress: {},

      // WorkOS Auth initial state
      authUser: null,
      organizations: [],
      isAuthenticated: false,
      authLoading: true,

      // Actions
      setTheme: (theme) => {
        document.documentElement.setAttribute('data-theme', theme);
        set({ theme });
      },

      toggleTheme: () => {
        const newTheme = get().theme === 'light' ? 'dark' : 'light';
        document.documentElement.setAttribute('data-theme', newTheme);
        set({ theme: newTheme });
      },

      setPlayer: (player) => set({ player, isLoggedIn: !!player }),

      logout: () => set({
        player: null,
        isLoggedIn: false,
      }),

      updateSkillProgress: (category, correct) => {
        const { skillProgress } = get();
        const current = skillProgress[category] || {
          category,
          questionsAnswered: 0,
          correctAnswers: 0,
          accuracy: 0,
          estimatedTheta: 0,
        };

        const newQuestionsAnswered = current.questionsAnswered + 1;
        const newCorrectAnswers = current.correctAnswers + (correct ? 1 : 0);
        const newAccuracy = newCorrectAnswers / newQuestionsAnswered;
        const newTheta = (newAccuracy - 0.5) * 4;

        set({
          skillProgress: {
            ...skillProgress,
            [category]: {
              category,
              questionsAnswered: newQuestionsAnswered,
              correctAnswers: newCorrectAnswers,
              accuracy: newAccuracy,
              estimatedTheta: newTheta,
            },
          },
        });
      },

      // WorkOS Auth actions
      setAuthState: (user, orgs) => set({
        authUser: user,
        organizations: orgs,
        isAuthenticated: !!user,
        authLoading: false,
      }),

      setAuthLoading: (loading) => set({ authLoading: loading }),

      clearAuth: () => set({
        authUser: null,
        organizations: [],
        isAuthenticated: false,
        authLoading: false,
      }),
    }),
    {
      name: 'balanced-arena-storage',
      partialize: (state) => ({
        theme: state.theme,
        player: state.player,
        isLoggedIn: state.isLoggedIn,
        skillProgress: state.skillProgress,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply theme on load
        if (state?.theme) {
          document.documentElement.setAttribute('data-theme', state.theme);
        }
      },
    }
  )
);
